import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Divider, Grid, IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';

import i18n from '../../i18n';
import StringItemSelector from './StringItemSelector';
import AddSingleFieldModal from './AddSingleFieldModal';
import GenericDeleteModal from './GenericDeleteModal'
import RequireScope from '../RequireScope';
import {
  addAgency,
  deleteAgency,
  fetchAgencies,
  updateAgency,
} from '../../helpers/externalRequests';
import EditSingleFieldModal from './EditSingleFieldModal';

class AgencyEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedAgencyId: 0,
      selectedAgencyName: null,
      agencies: [],
      agencyNames: [],
      showDeleteModal: false,
      showEditModal: false,
      showAddModal: false
    };
  }

  componentDidMount = () => {
    this.fetchAgencies();
  };

  createAgency = async (agencyName) => {
    const { openSnackbar } = this.props;
    try {
      const response = await addAgency({ agencyName });
      this.hideAddModal();
      openSnackbar(
        `${i18n.t('setting:successAgencyCreate')}: ${
          response.data[0].agencyName
        }`
      );
      this.fetchAgencies();
    }
    catch (error) {
      openSnackbar(`${i18n.t('setting:errorAgencyCreate')}: ${error}`);
    }
  };

  deleteAgency = async (selectedAgencyId) => {
    const { openSnackbar } = this.props;
    try {
      const response = await deleteAgency({ id: selectedAgencyId });
      if (response.data && response.data.error) {
        openSnackbar(i18n.t('setting:errorAgencyDeleteAssociatedAgents'));
      } else {
        this.hideDeleteModal();
        openSnackbar(i18n.t('setting:successAgencyDelete'));
        this.setState(
          { selectedAgencyName: null, selectedAgencyId: 0 },
          () => this.fetchAgencies()
        );
      }
    }
    catch {
      openSnackbar(i18n.t('setting:errorAgencyDelete'));
    }
  };

  fetchAgencies = async () => {
    const { updateAgencies } = this.props;
    const agencies = await fetchAgencies();
    const agencyNames = agencies.data.map(agency => agency.name);
    this.setState(
      { agencies: agencies.data, agencyNames },
      () => updateAgencies(agencies.data)
    );
  };

  onAgencyChange = event => {
    const { value } = event.target;
    if (value) {
      this.setState(prevState => {
        const selectedAgencyId = value;
        const selectedAgency = prevState.agencies.find(
          agency => agency.id === value
        );
        return {
          selectedAgencyName: selectedAgency.label,
          selectedAgencyId
        };
      });
    }
  };

  saveAgency = async (updatedAgencyName) => {
    const { selectedAgencyId } = this.state;
    const { openSnackbar } = this.props;
    try {
      const response = await updateAgency(
        { id: selectedAgencyId, agencyName: updatedAgencyName }
      );  
      this.hideEditModal();
      if (response.data[0] === 1) {
        openSnackbar(i18n.t('setting:successAgencyUpdate'));
        this.fetchAgencies();
        this.setState({ selectedAgencyName: updatedAgencyName });
      } else {
        openSnackbar(i18n.t('setting:errorAgencyUpdate'));
      }
    } catch(error) {
      openSnackbar(`${i18n.t('setting:errorAgencyUpdate')} ${error}`);
    }
  };

  showDeleteModal = () => {
    this.setState({ showDeleteModal: true });
  };

  hideDeleteModal = () => {
    this.setState({ showDeleteModal: false });
  };

  showEditModal = () => {
    this.setState({ showEditModal: true });
  };

  hideEditModal = () => {
    this.setState({ showEditModal: false });
  };

  showAddModal = () => {
    this.setState({ showAddModal: true });
  };

  hideAddModal = () => {
    this.setState({ showAddModal: false });
  };

render() {
  const {
    selectedAgencyId,
    selectedAgencyName,
    agencies,
    agencyNames,
    showAddModal,
    showDeleteModal,
    showEditModal
  } = this.state;

  return (
    <Grid container>
      <Grid item xs={7}>
        <h6>{i18n.t('setting:manageAgencies')}</h6>
        <Divider />
        <br />
        <StringItemSelector
          selectedItem={selectedAgencyId}
          items={agencies}
          onItemChange={this.onAgencyChange}
          label={i18n.t('setting:chooseAgency')}
          helperText={i18n.t('setting:agencyHelp')}
        />
        <RequireScope scopes={['entities:write']}>
          <IconButton
            color="secondary"
            aria-label="add"
            onClick={this.showAddModal}
          >
            <AddIcon />
          </IconButton>
          <IconButton
            aria-label="edit"
            color="secondary"
            onClick={this.showEditModal}
          >
            <EditIcon />
          </IconButton>

          <IconButton
            aria-label="delete"
            color="secondary"
            onClick={this.showDeleteModal}
          >
            <DeleteIcon />
          </IconButton>
        </RequireScope>
      </Grid>
      <AddSingleFieldModal
        confirmMessage={i18n.t('setting:addAgencyConfirm')}
        instruction={i18n.t('setting:enterAgency')}
        fieldLabel={i18n.t('setting:agencyName')}
        fieldName="AgencyAddTextField"
        showModal={showAddModal}
        onAddField={this.createAgency}
        onClose={this.hideAddModal}
        existingFieldNames={agencyNames}
      />
      <EditSingleFieldModal
        selectedItem={selectedAgencyName}
        showModal={showEditModal}
        title={i18n.t('setting:editAgencyTitle')}
        onClose={this.hideEditModal}
        onSave={this.saveAgency}
        existingFieldNames={agencyNames}
        warningText={i18n.t('setting:warningAgencyModal')}
      />
      <GenericDeleteModal
        showModal={showDeleteModal}
        noSelectionTitle={i18n.t('setting:warningAgencyModal')}
        confirmMessage={`${i18n.t(
          'setting:confirmDeleteAgency'
        )}: ${selectedAgencyName}?`}
        selectedId={selectedAgencyId}
        onClose={this.hideDeleteModal}
        onDelete={this.deleteAgency}
      />
    </Grid>
    );
  }
}

AgencyEditor.propTypes = {
  openSnackbar: PropTypes.func.isRequired,
  updateAgencies: PropTypes.func.isRequired
}

export default AgencyEditor;
